// PageDescription: 의약품 사전 홈페이지
import MedicineDictionaryTemplate from '@/components/templates/medicine-dictionary/MedicineDictionaryTemplate';
import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import useSearchTotalMedicineDictionaryViewModel from '@/features/medicine-dictionary/hook/useSearchTotalMedicineDictionaryViewModel';
import useMedicineDictionaryViewModel from '@/hooks/medicine-dictionary/useMedicineDictionaryViewModel';
import usePopularMedicineDictionaryItems from '@/hooks/medicine-dictionary/usePopularMedicineDictionaryItems';
import useGetKeywordIntegratedSearchMedicineDictionary from '@/hooks/search/useGetKeywordIntegratedSearchMedicineDictionary';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

const createMedicineDictionaryLdJson = (medicines: Array<MedicineDictionaryModel>, page: number) => {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MEDICINE_DICTIONARY.HOME.path}${addQueryString({
        page,
      })}`,
      url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MEDICINE_DICTIONARY.HOME.path}${addQueryString({
        page,
      })}`,
      name: '닥터나우 의약품 사전 | 약의 효능부터 복용법, 부작용까지 - 닥터나우',
      description:
        '어떤 약인지 궁금하다면? 닥터나우 의약품 사전에서 효능·효과, 복용법, 주의사항, 부작용까지 한눈에 확인하세요.',
      publisher: {
        '@type': 'Organization',
        name: '닥터나우',
        url: 'https://doctornow.co.kr',
        logo: {
          '@type': 'ImageObject',
          url: 'https://media.doctornow.co.kr/og-main.png',
        },
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'contact@doctornow.co.kr',
          },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MEDICINE_DICTIONARY.HOME.path}${addQueryString({
        page,
      })}#itemList`,
      name: '닥터나우 의약품 사전 | 약의 효능부터 복용법, 부작용까지 - 닥터나우',
      description:
        '어떤 약인지 궁금하다면? 닥터나우 의약품 사전에서 효능·효과, 복용법, 주의사항, 부작용까지 한눈에 확인하세요.',
      numberOfItems: medicines.length,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: medicines.map((medicine, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MEDICINE_DICTIONARY.DETAIL.path}/${medicine.getPublicId()}`,
        name: medicine.getBasicInfoName(),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '닥터나우',
      url: 'https://doctornow.co.kr',
      logo: 'https://media.doctornow.co.kr/og-main.png',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@doctornow.co.kr',
        },
      ],
    },
  ];
};

const MedicineDictionaryPage = ({ client }: CustomPageProps) => {
  const viewModel = useMedicineDictionaryViewModel();
  const medicineViewModel = useSearchTotalMedicineDictionaryViewModel();

  return (
    <>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              createMedicineDictionaryLdJson(medicineViewModel.medicineDictionaryItems, medicineViewModel.currentPage)
            ),
          }}
        />
      </Head>
      <NextSeo
        title={'닥터나우 의약품 사전 | 약의 효능부터 복용법, 부작용까지 - 닥터나우'}
        description={
          '어떤 약인지 궁금하다면? 닥터나우 의약품 사전에서 효능·효과, 복용법, 주의사항, 부작용까지 한눈에 확인하세요.'
        }
        additionalMetaTags={[
          {
            name: 'keywords',
            content: '의약품 사전, 약 효능, 약 부작용, 복용법, 약 정보',
          },
        ]}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MEDICINE_DICTIONARY.HOME.path}${addQueryString(
            viewModel.queries
          )}`,
        }}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MEDICINE_DICTIONARY.HOME.path}${addQueryString(
          viewModel.queries
        )}`}
      />
      <MedicineDictionaryTemplate viewModel={viewModel} client={client} medicineViewModel={medicineViewModel} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (context) => {
  const queryClient = new QueryClient();
  const page = Number(context.query.page) || 1;

  await Promise.allSettled([
    usePopularMedicineDictionaryItems.prefetchQuery(queryClient),
    useGetKeywordIntegratedSearchMedicineDictionary.prefetchQuery(queryClient, {
      keyword: undefined,
      limit: 10,
      page,
    }),
  ]);

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      page,
    },
  };
});

export default MedicineDictionaryPage;
